import React, { useState, useEffect, useCallback } from 'react';
import { listInvoices, sendMultipleDraftInvoicesToXero, syncMultipleInvoicesWithXero, voidInvoice } from '../services/invoiceService';
import { listRCTIs, sendMultipleDraftRCTIsToXero, syncMultipleRCTIsWithXero, voidRCTI } from '../services/rctiService';
import { listFranchiseeInvoices, syncFranchiseeInvoicesWithXero } from '../services/franchiseeInvoiceService';
import { checkForDuplicates, checkForInvoiceDuplicates, listDuplicates } from '../services/duplicateDetectionService';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FaFileInvoiceDollar, FaPaperPlane, FaExclamationCircle, FaSync, FaLink, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import InfoModal from '../components/InfoModal';
import SyncResultsModal from '../components/SyncResultsModal';
import ConfirmationModal from '../components/ConfirmationModal';
import DuplicatesModal from '../components/DuplicatesModal';
import InvoiceTabContent from '../components/InvoiceTabContent';
import RCTITabContent from '../components/RCTITabContent';
import FranchiseeInvoiceTabContent from '../components/FranchiseeInvoiceTabContent';
import { formatDate } from '../utils/dateUtils';
import { checkXeroConnection, initiateXeroConnection, handleXeroRedirectStatus } from '../utils/xeroUtils';
import { isDemoMode } from '../utils/demoMode';
import useDemoPermissions from '../hooks/useDemoPermissions';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'awaiting_approval', label: 'Awaiting Approval' },
  { value: 'awaiting_payment', label: 'Awaiting Payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

const calculateAmountDue = (invoice) => {
  if (invoice.Quantity == null || invoice.UnitAmount == null) return 'N/A';
  const amount = invoice.Quantity * invoice.UnitAmount;
  return `$${amount.toFixed(2)}`;
};

const InvoicesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDemo } = useDemoPermissions();
  
  // Get active tab from URL params, default to 'invoices'
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'rctis') return 'rctis';
    if (tabFromUrl === 'franchisee-invoices') return 'franchisee-invoices';
    return 'invoices';
  });
  
  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [invoiceError, setInvoiceError] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState(() => searchParams.get('invoiceStatus') || '');
  const [sendingInvoicesToXero, setSendingInvoicesToXero] = useState(false);
  const [invoiceXeroBatchResponse, setInvoiceXeroBatchResponse] = useState(null);
  const [invoiceSyncing, setInvoiceSyncing] = useState(false);
  const [invoiceSyncResults, setInvoiceSyncResults] = useState(null);
  const [voidingInvoiceId, setVoidingInvoiceId] = useState(null);
  const [voidConfirmModal, setVoidConfirmModal] = useState({ isOpen: false, invoiceId: null, invoiceNumber: null });
  const [voidingRCTIId, setVoidingRCTIId] = useState(null);
  const [voidRCTIConfirmModal, setVoidRCTIConfirmModal] = useState({ isOpen: false, rctiId: null, rctiNumber: null });
  const [invoiceFilterFseCode, setInvoiceFilterFseCode] = useState(() => searchParams.get('invoiceFseCode') || null);
  const [invoiceFilterHouseholdId, setInvoiceFilterHouseholdId] = useState(() => searchParams.get('invoiceHouseholdId') || searchParams.get('invoiceClientId') || null);
  const [invoiceFilterXeroStatus, setInvoiceFilterXeroStatus] = useState(() => searchParams.get('invoiceXeroStatus') || '');
  const [invoiceFilterInvoiceNumber, setInvoiceFilterInvoiceNumber] = useState(() => searchParams.get('invoiceNumber') || '');
  const [invoiceSortDirection, setInvoiceSortDirection] = useState(() => searchParams.get('invoiceSort') || 'desc');
  
  // Invoice pagination state
  const [invoicePageSize, setInvoicePageSize] = useState(30);
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [invoicePageDocIds, setInvoicePageDocIds] = useState([null]); // [null, docId1, docId2, ...]
  const [invoiceHasNextPage, setInvoiceHasNextPage] = useState(false);
  const [invoicePageNavigating, setInvoicePageNavigating] = useState(false);
  
  // RCTI state
  const [rctis, setRCTIs] = useState([]);
  const [rctiLoading, setRCTILoading] = useState(true);
  const [rctiError, setRCTIError] = useState(null);
  const [selectedRCTIs, setSelectedRCTIs] = useState(new Set());
  const [selectedRCTIStatus, setSelectedRCTIStatus] = useState(() => searchParams.get('rctiStatus') || '');
  const [sendingRCTIsToXero, setSendingRCTIsToXero] = useState(false);
  const [rctiXeroBatchResponse, setRCTIXeroBatchResponse] = useState(null);
  const [rctiSyncing, setRCTISyncing] = useState(false);
  const [rctiSyncResults, setRCTISyncResults] = useState(null);
  const [rctiFilterFseCode, setRCTIFilterFseCode] = useState(() => searchParams.get('rctiFseCode') || null);
  const [rctiFilterHouseholdId, setRCTIFilterHouseholdId] = useState(() => searchParams.get('rctiHouseholdId') || searchParams.get('rctiClientId') || null);
  const [rctiSortDirection, setRCTISortDirection] = useState(() => searchParams.get('rctiSort') || 'desc');
  const [rctiFilterXeroStatus, setRCTIFilterXeroStatus] = useState(() => searchParams.get('rctiXeroStatus') || '');
  const [rctiFilterRctiNumber, setRCTIFilterRctiNumber] = useState(() => searchParams.get('rctiNumber') || '');

  // RCTI pagination state
  const [rctiPageSize, setRCTIPageSize] = useState(30);
  const [rctiCurrentPage, setRCTICurrentPage] = useState(1);
  const [rctiPageDocIds, setRCTIPageDocIds] = useState([null]); // [null, docId1, docId2, ...]
  const [rctiHasNextPage, setRCTIHasNextPage] = useState(false);
  const [rctiPageNavigating, setRCTIPageNavigating] = useState(false);

  // Franchisee Invoice state
  const [franchiseeInvoices, setFranchiseeInvoices] = useState([]);
  const [franchiseeInvoiceLoading, setFranchiseeInvoiceLoading] = useState(true);
  const [franchiseeInvoiceError, setFranchiseeInvoiceError] = useState(null);
  const [franchiseeInvoiceSyncing, setFranchiseeInvoiceSyncing] = useState(false);
  const [franchiseeInvoiceFilterFseCode, setFranchiseeInvoiceFilterFseCode] = useState(() => searchParams.get('franchiseeInvoiceFseCode') || null);
  const [franchiseeInvoiceFilterStatus, setFranchiseeInvoiceFilterStatus] = useState(() => searchParams.get('franchiseeInvoiceStatus') || '');
  const [franchiseeInvoiceSortDirection, setFranchiseeInvoiceSortDirection] = useState(() => searchParams.get('franchiseeInvoiceSort') || 'desc');
  const [isFranchiseeInvoiceSyncConfirmOpen, setIsFranchiseeInvoiceSyncConfirmOpen] = useState(false);

  // Franchisee Invoice pagination state
  const [franchiseeInvoicePageSize, setFranchiseeInvoicePageSize] = useState(30);
  const [franchiseeInvoiceCurrentPage, setFranchiseeInvoiceCurrentPage] = useState(1);
  const [franchiseeInvoicePageDocIds, setFranchiseeInvoicePageDocIds] = useState([null]);
  const [franchiseeInvoiceHasNextPage, setFranchiseeInvoiceHasNextPage] = useState(false);
  const [franchiseeInvoicePageNavigating, setFranchiseeInvoicePageNavigating] = useState(false);

  // Shared modal state
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({ title: '', message: '' });
  const [isSyncResultsModalOpen, setIsSyncResultsModalOpen] = useState(false);

  // Xero connection state
  const [xeroConnected, setXeroConnected] = useState(null); // null = unknown, true = connected, false = not connected
  const [isConnectingToXero, setIsConnectingToXero] = useState(false);
  const [autoSyncTab, setAutoSyncTab] = useState(null);

  // Duplicate detection state - RCTI
  const [rctiDuplicates, setRctiDuplicates] = useState([]);
  const [rctiDuplicatesCount, setRctiDuplicatesCount] = useState(0);
  const [checkingRctiDuplicates, setCheckingRctiDuplicates] = useState(false);
  const [isRctiDuplicatesModalOpen, setIsRctiDuplicatesModalOpen] = useState(false);

  // Duplicate detection state - Invoice
  const [invoiceDuplicates, setInvoiceDuplicates] = useState([]);
  const [invoiceDuplicatesCount, setInvoiceDuplicatesCount] = useState(0);
  const [checkingInvoiceDuplicates, setCheckingInvoiceDuplicates] = useState(false);
  const [isInvoiceDuplicatesModalOpen, setIsInvoiceDuplicatesModalOpen] = useState(false);

  const resetInvoicePaginationAndSelection = useCallback(() => {
    setInvoiceCurrentPage(1);
    setInvoicePageDocIds([null]);
    setInvoiceHasNextPage(false);
    setSelectedInvoices(new Set());
  }, []);

  const clearInvoiceFilters = useCallback((options = {}) => {
    setSelectedInvoiceStatus('');
    setInvoiceFilterFseCode(null);
    setInvoiceFilterHouseholdId(null);
    setInvoiceFilterXeroStatus('');
    setInvoiceFilterInvoiceNumber('');
    setInvoiceSortDirection('desc');
    resetInvoicePaginationAndSelection();

    if (options.updateSearchParams !== false) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        ['invoiceStatus', 'invoiceFseCode', 'invoiceHouseholdId', 'invoiceClientId', 'invoiceSort', 'invoiceXeroStatus', 'invoiceNumber'].forEach(key => newParams.delete(key));
        return newParams;
      });
    }
  }, [resetInvoicePaginationAndSelection, setSearchParams]);

  const resetRCTIPaginationAndSelection = useCallback(() => {
    setRCTICurrentPage(1);
    setRCTIPageDocIds([null]);
    setRCTIHasNextPage(false);
    setSelectedRCTIs(new Set());
  }, []);

  const clearRCTIFilters = useCallback((options = {}) => {
    setSelectedRCTIStatus('');
    setRCTIFilterFseCode(null);
    setRCTIFilterHouseholdId(null);
    setRCTISortDirection('desc');
    setRCTIFilterXeroStatus('');
    setRCTIFilterRctiNumber('');
    resetRCTIPaginationAndSelection();

    if (options.updateSearchParams !== false) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        ['rctiStatus', 'rctiFseCode', 'rctiHouseholdId', 'rctiClientId', 'rctiSort', 'rctiXeroStatus', 'rctiNumber'].forEach(key => newParams.delete(key));
        return newParams;
      });
    }
  }, [resetRCTIPaginationAndSelection, setSearchParams]);

  // Persist invoice filter/search state in URL for navigation durability
  const persistInvoiceFiltersToSearchParams = useCallback((overrides = {}) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      ['invoiceClientId'].forEach(key => newParams.delete(key));
      const nextValues = {
        invoiceStatus: overrides.status !== undefined ? overrides.status : selectedInvoiceStatus,
        invoiceFseCode: overrides.fseCode !== undefined ? overrides.fseCode : invoiceFilterFseCode,
        invoiceHouseholdId: overrides.householdId !== undefined ? overrides.householdId : invoiceFilterHouseholdId,
        invoiceSort: overrides.sortDirection !== undefined ? overrides.sortDirection : invoiceSortDirection,
        invoiceXeroStatus: overrides.xeroStatus !== undefined ? overrides.xeroStatus : invoiceFilterXeroStatus,
        invoiceNumber: overrides.invoiceNumber !== undefined ? overrides.invoiceNumber : invoiceFilterInvoiceNumber,
      };

      Object.entries(nextValues).forEach(([key, value]) => {
        if (value === null || value === '' || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      return newParams;
    });
  }, [invoiceFilterFseCode, invoiceFilterHouseholdId, invoiceSortDirection, selectedInvoiceStatus, invoiceFilterXeroStatus, invoiceFilterInvoiceNumber, setSearchParams]);

  // Persist RCTI filter/search state in URL for navigation durability
  const persistRCTIFiltersToSearchParams = useCallback((overrides = {}) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      ['rctiClientId'].forEach(key => newParams.delete(key));
      const nextValues = {
        rctiStatus: overrides.status !== undefined ? overrides.status : selectedRCTIStatus,
        rctiFseCode: overrides.fseCode !== undefined ? overrides.fseCode : rctiFilterFseCode,
        rctiHouseholdId: overrides.householdId !== undefined ? overrides.householdId : rctiFilterHouseholdId,
        rctiSort: overrides.sortDirection !== undefined ? overrides.sortDirection : rctiSortDirection,
        rctiXeroStatus: overrides.xeroStatus !== undefined ? overrides.xeroStatus : rctiFilterXeroStatus,
        rctiNumber: overrides.rctiNumber !== undefined ? overrides.rctiNumber : rctiFilterRctiNumber,
      };

      Object.entries(nextValues).forEach(([key, value]) => {
        if (value === null || value === '' || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      return newParams;
    });
  }, [rctiFilterFseCode, rctiFilterHouseholdId, rctiSortDirection, selectedRCTIStatus, rctiFilterXeroStatus, rctiFilterRctiNumber, setSearchParams]);

  const resetFranchiseeInvoicePagination = useCallback(() => {
    setFranchiseeInvoiceCurrentPage(1);
    setFranchiseeInvoicePageDocIds([null]);
    setFranchiseeInvoiceHasNextPage(false);
  }, []);

  const clearFranchiseeInvoiceFilters = useCallback((options = {}) => {
    setFranchiseeInvoiceFilterFseCode(null);
    setFranchiseeInvoiceFilterStatus('');
    setFranchiseeInvoiceSortDirection('desc');
    resetFranchiseeInvoicePagination();

    if (options.updateSearchParams !== false) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        ['franchiseeInvoiceFseCode', 'franchiseeInvoiceStatus', 'franchiseeInvoiceSort'].forEach(key => newParams.delete(key));
        return newParams;
      });
    }
  }, [resetFranchiseeInvoicePagination, setSearchParams]);

  const persistFranchiseeInvoiceFiltersToSearchParams = useCallback((overrides = {}) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      const nextValues = {
        franchiseeInvoiceFseCode: overrides.fseCode !== undefined ? overrides.fseCode : franchiseeInvoiceFilterFseCode,
        franchiseeInvoiceStatus: overrides.status !== undefined ? overrides.status : franchiseeInvoiceFilterStatus,
        franchiseeInvoiceSort: overrides.sortDirection !== undefined ? overrides.sortDirection : franchiseeInvoiceSortDirection,
      };

      Object.entries(nextValues).forEach(([key, value]) => {
        if (value === null || value === '' || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      return newParams;
    });
  }, [franchiseeInvoiceFilterFseCode, franchiseeInvoiceFilterStatus, franchiseeInvoiceSortDirection, setSearchParams]);

  const fetchInvoices = useCallback(async (page = 1, statusOverride = undefined, filters = {}) => {
    setInvoiceLoading(true);
    setInvoiceError(null);

    // Save scroll position
    const scrollPosition = window.scrollY;

    try {
      const startAfterDocId = invoicePageDocIds[page - 1];

      // Use passed filters or fall back to current state
      const effectiveFilters = {
        fseCode: filters.fseCode !== undefined ? filters.fseCode : invoiceFilterFseCode,
        householdId: filters.householdId !== undefined ? filters.householdId : invoiceFilterHouseholdId,
        sortDirection: filters.sortDirection !== undefined ? filters.sortDirection : invoiceSortDirection,
        xeroStatus: filters.xeroStatus !== undefined ? filters.xeroStatus : invoiceFilterXeroStatus,
        invoiceNumber: filters.invoiceNumber !== undefined ? filters.invoiceNumber : invoiceFilterInvoiceNumber,
      };

      const params = {
        pageSize: invoicePageSize,
        orderBy: 'InvoiceDate',
        orderDirection: effectiveFilters.sortDirection,
        startAfterDocId,
      };
      const statusToUse = statusOverride !== undefined ? statusOverride : selectedInvoiceStatus;
      if (statusToUse) {
        params.status = statusToUse;
      }
      if (effectiveFilters.fseCode) {
        params.fseCode = effectiveFilters.fseCode;
      }
      if (effectiveFilters.householdId) {
        params.householdId = effectiveFilters.householdId;
      }
      if (effectiveFilters.xeroStatus) {
        params.xeroStatus = effectiveFilters.xeroStatus;
      }
      if (effectiveFilters.invoiceNumber) {
        params.invoiceNumber = effectiveFilters.invoiceNumber;
      }
      console.log('params', params)
      const data = await listInvoices(params);
      
      setInvoices(data.invoices);
      setInvoiceHasNextPage(!!data.nextPageDocId);
      
      // Update page doc IDs for next page navigation
      if (data.nextPageDocId && !invoicePageDocIds[page]) {
        setInvoicePageDocIds(prev => {
          const newDocIds = [...prev];
          newDocIds[page] = data.nextPageDocId;
          return newDocIds;
        });
      }

      return data;
    } catch (err) {
      setInvoiceError(err.message || 'Failed to load invoices.');
      return null;
    } finally {
      setInvoiceLoading(false);
      // Restore scroll position after a brief delay
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    }
  }, [selectedInvoiceStatus, invoicePageSize, invoicePageDocIds, invoiceFilterFseCode, invoiceFilterHouseholdId, invoiceSortDirection, invoiceFilterXeroStatus, invoiceFilterInvoiceNumber]);

  const applyInvoiceFilters = useCallback((overrides = {}) => {
    const nextValues = {
      status: overrides.status !== undefined ? overrides.status : selectedInvoiceStatus,
      fseCode: overrides.fseCode !== undefined ? overrides.fseCode : invoiceFilterFseCode,
      householdId: overrides.householdId !== undefined ? overrides.householdId : invoiceFilterHouseholdId,
      sortDirection: overrides.sortDirection !== undefined ? overrides.sortDirection : invoiceSortDirection,
      xeroStatus: overrides.xeroStatus !== undefined ? overrides.xeroStatus : invoiceFilterXeroStatus,
      invoiceNumber: overrides.invoiceNumber !== undefined ? overrides.invoiceNumber : invoiceFilterInvoiceNumber,
    };

    resetInvoicePaginationAndSelection();
    persistInvoiceFiltersToSearchParams({
      status: nextValues.status,
      fseCode: nextValues.fseCode,
      householdId: nextValues.householdId,
      sortDirection: nextValues.sortDirection,
      xeroStatus: nextValues.xeroStatus,
      invoiceNumber: nextValues.invoiceNumber,
    });

    fetchInvoices(1, nextValues.status, {
      fseCode: nextValues.fseCode,
      householdId: nextValues.householdId,
      sortDirection: nextValues.sortDirection,
      xeroStatus: nextValues.xeroStatus,
      invoiceNumber: nextValues.invoiceNumber,
    });
  }, [fetchInvoices, invoiceFilterFseCode, invoiceFilterHouseholdId, invoiceSortDirection, persistInvoiceFiltersToSearchParams, resetInvoicePaginationAndSelection, selectedInvoiceStatus, invoiceFilterXeroStatus, invoiceFilterInvoiceNumber]);

  const fetchRCTIs = useCallback(async (page = 1, statusOverride = undefined, filters = {}) => {
    setRCTILoading(true);
    setRCTIError(null);

    // Save scroll position
    const scrollPosition = window.scrollY;

    try {
      const startAfterDocId = rctiPageDocIds[page - 1];

      // Use passed filters or fall back to current state
      const effectiveFilters = {
        fseCode: filters.fseCode !== undefined ? filters.fseCode : rctiFilterFseCode,
        householdId: filters.householdId !== undefined ? filters.householdId : rctiFilterHouseholdId,
        sortDirection: filters.sortDirection !== undefined ? filters.sortDirection : rctiSortDirection,
        xeroStatus: filters.xeroStatus !== undefined ? filters.xeroStatus : rctiFilterXeroStatus,
        rctiNumber: filters.rctiNumber !== undefined ? filters.rctiNumber : rctiFilterRctiNumber,
      };

      const params = {
        pageSize: rctiPageSize,
        orderBy: 'RCTIDate',
        orderDirection: effectiveFilters.sortDirection,
        startAfterDocId,
      };
      const statusToUse = statusOverride !== undefined ? statusOverride : selectedRCTIStatus;
      if (statusToUse) {
        params.status = statusToUse;
      }
      if (effectiveFilters.fseCode) {
        params.fseCode = effectiveFilters.fseCode;
      }
      if (effectiveFilters.householdId) {
        params.householdId = effectiveFilters.householdId;
      }
      if (effectiveFilters.xeroStatus) {
        params.xeroStatus = effectiveFilters.xeroStatus;
      }
      if (effectiveFilters.rctiNumber) {
        params.rctiNumber = effectiveFilters.rctiNumber;
      }
      const data = await listRCTIs(params);
      
      setRCTIs(data.rctis);
      setRCTIHasNextPage(!!data.nextPageDocId);
      
      // Update page doc IDs for next page navigation
      if (data.nextPageDocId && !rctiPageDocIds[page]) {
        setRCTIPageDocIds(prev => {
          const newDocIds = [...prev];
          newDocIds[page] = data.nextPageDocId;
          return newDocIds;
        });
      }

      return data;
    } catch (err) {
      setRCTIError(err.message || 'Failed to load RCTIs.');
      return null;
    } finally {
      setRCTILoading(false);
      // Restore scroll position after a brief delay
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    }
  }, [selectedRCTIStatus, rctiPageSize, rctiPageDocIds, rctiFilterFseCode, rctiFilterHouseholdId, rctiSortDirection, rctiFilterXeroStatus, rctiFilterRctiNumber]);

  const applyRCTIFilters = useCallback((overrides = {}) => {
    const nextValues = {
      status: overrides.status !== undefined ? overrides.status : selectedRCTIStatus,
      fseCode: overrides.fseCode !== undefined ? overrides.fseCode : rctiFilterFseCode,
      householdId: overrides.householdId !== undefined ? overrides.householdId : rctiFilterHouseholdId,
      sortDirection: overrides.sortDirection !== undefined ? overrides.sortDirection : rctiSortDirection,
      xeroStatus: overrides.xeroStatus !== undefined ? overrides.xeroStatus : rctiFilterXeroStatus,
      rctiNumber: overrides.rctiNumber !== undefined ? overrides.rctiNumber : rctiFilterRctiNumber,
    };

    resetRCTIPaginationAndSelection();
    persistRCTIFiltersToSearchParams({
      status: nextValues.status,
      fseCode: nextValues.fseCode,
      householdId: nextValues.householdId,
      sortDirection: nextValues.sortDirection,
      xeroStatus: nextValues.xeroStatus,
      rctiNumber: nextValues.rctiNumber,
    });

    fetchRCTIs(1, nextValues.status, {
      fseCode: nextValues.fseCode,
      householdId: nextValues.householdId,
      sortDirection: nextValues.sortDirection,
      xeroStatus: nextValues.xeroStatus,
      rctiNumber: nextValues.rctiNumber,
    });
  }, [fetchRCTIs, persistRCTIFiltersToSearchParams, resetRCTIPaginationAndSelection, rctiFilterFseCode, rctiFilterHouseholdId, rctiSortDirection, selectedRCTIStatus, rctiFilterXeroStatus, rctiFilterRctiNumber]);

  // Check Xero connection status using shared utility
  const checkXeroConnectionStatus = useCallback(async () => {
    try {
      const isConnected = await checkXeroConnection();
      setXeroConnected(isConnected);
    } catch (error) {
      console.error('Error checking Xero connection:', error);
      setXeroConnected(false);
    }
  }, []);

  // Handle Xero connection using shared utility
  // Handle Xero connection using shared utility
  const handleConnectXero = async () => {
    setIsConnectingToXero(true);
    try {
      const authUrl = await initiateXeroConnection();
      // Redirect the browser to the URL provided by the backend
      window.location.href = authUrl;
    } catch (error) {
      setInfoModalContent({
        title: "Xero Connection Error",
        message: error.message
      });
      setIsInfoModalOpen(true);
      setIsConnectingToXero(false);
    }
  };

  const runInvoiceAutoSync = useCallback(async (invoiceList = []) => {
    if (isDemo || isDemoMode()) return;
    const syncableInvoiceIds = invoiceList
      .filter(invoice => invoice.xero_submission_status === 'success' && invoice.xeroInvoiceId)
      .map(invoice => invoice.id);

    if (syncableInvoiceIds.length === 0) {
      return;
    }

    setAutoSyncTab('invoices');

    try {
      await syncMultipleInvoicesWithXero(syncableInvoiceIds);
      await fetchInvoices(invoiceCurrentPage);
    } catch (error) {
      console.error('Background invoice sync failed:', error);
      checkXeroConnectionStatus();
    } finally {
      setAutoSyncTab(currentTab => currentTab === 'invoices' ? null : currentTab);
    }
  }, [checkXeroConnectionStatus, fetchInvoices, invoiceCurrentPage]);

  const runRCTIAutoSync = useCallback(async (rctiList = []) => {
    if (isDemo || isDemoMode()) return;
    const syncableRCTIIds = rctiList
      .filter(rcti => rcti.xero_submission_status === 'success' && rcti.xeroInvoiceId)
      .map(rcti => rcti.id);

    if (syncableRCTIIds.length === 0) {
      return;
    }

    setAutoSyncTab('rctis');

    try {
      await syncMultipleRCTIsWithXero(syncableRCTIIds);
      await fetchRCTIs(rctiCurrentPage);
    } catch (error) {
      console.error('Background RCTI sync failed:', error);
      checkXeroConnectionStatus();
    } finally {
      setAutoSyncTab(currentTab => currentTab === 'rctis' ? null : currentTab);
    }
  }, [checkXeroConnectionStatus, fetchRCTIs, rctiCurrentPage]);

  // Initial load and tab switching only
  useEffect(() => {
    let isCancelled = false;

    const loadActiveTab = async () => {
      if (activeTab === 'invoices') {
        const data = await fetchInvoices(invoiceCurrentPage);
        if (!isCancelled) {
          setSelectedRCTIs(new Set());
          await runInvoiceAutoSync(data?.invoices || []);
        }
      } else if (activeTab === 'rctis') {
        const data = await fetchRCTIs(rctiCurrentPage);
        if (!isCancelled) {
          setSelectedInvoices(new Set());
          await runRCTIAutoSync(data?.rctis || []);
        }
      } else if (activeTab === 'franchisee-invoices') {
        await fetchFranchiseeInvoices(franchiseeInvoiceCurrentPage);
        if (!isCancelled) {
          setSelectedInvoices(new Set());
          setSelectedRCTIs(new Set());
        }
      }
    };

    loadActiveTab();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Check Xero connection status on component mount — skip entirely in demo mode
  useEffect(() => {
    if (isDemo) {
      // In demo mode, mark Xero as not connected immediately so no spinner appears
      setXeroConnected(false);
      return;
    }
    if (!isDemoMode()) checkXeroConnectionStatus();
  }, [isDemo, checkXeroConnectionStatus]);

  // Check for Xero redirect status on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const redirectStatus = handleXeroRedirectStatus(queryParams);

    if (redirectStatus) {
      setXeroConnected(redirectStatus.success);
      setInfoModalContent({
        title: redirectStatus.success ? "Xero Connection Successful" : "Xero Connection Failed",
        message: redirectStatus.message
      });
      setIsInfoModalOpen(true);
      
      // Clear query params from URL after reading them
      window.history.replaceState({}, document.title, location.pathname + (location.search.includes('tab=') ? '?tab=rctis' : ''));
    }
  }, [location]);

  // Check for Xero redirect status on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const redirectStatus = handleXeroRedirectStatus(queryParams);

    if (redirectStatus) {
      setXeroConnected(redirectStatus.success);
      setInfoModalContent({
        title: redirectStatus.success ? "Xero Connection Successful" : "Xero Connection Failed",
        message: redirectStatus.message
      });
      setIsInfoModalOpen(true);
      
      // Clear query params from URL after reading them
      window.history.replaceState({}, document.title, location.pathname + (location.search.includes('tab=') ? '?tab=rctis' : ''));
    }
  }, [location]);

  const handleInvoiceStatusChange = (event) => {
    const newStatus = event.target.value;
    setSelectedInvoiceStatus(newStatus);
    applyInvoiceFilters({ status: newStatus });
  };

  const handleRCTIStatusChange = (event) => {
    const newStatus = event.target.value;
    setSelectedRCTIStatus(newStatus);
    applyRCTIFilters({ status: newStatus });
  };

  const handleSelectInvoice = (invoiceId, status) => {
    if (status !== 'draft') return;

    setSelectedInvoices(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(invoiceId)) {
        newSelected.delete(invoiceId);
      } else {
        newSelected.add(invoiceId);
      }
      return newSelected;
    });
  };

  const handleSelectRCTI = (rctiId, status) => {
    // Find the RCTI to check if it should be selectable
    const rcti = rctis.find(r => r.id === rctiId);
    if (!rcti) return;

    // Allow selection for any RCTI that has not yet been sent to Xero successfully.
    const isSelectable = rcti.xero_submission_status !== 'success';

    if (!isSelectable) return;

    setSelectedRCTIs(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(rctiId)) {
        newSelected.delete(rctiId);
      } else {
        newSelected.add(rctiId);
      }
      return newSelected;
    });
  };

  const handleSelectAllDraftInvoices = () => {
    const selectableDraftInvoices = invoices.filter(
      inv => inv.status === 'draft' && inv.xero_submission_status !== 'success'
    );
    const selectableInvoiceIds = selectableDraftInvoices.map(inv => inv.id);

    const allCurrentlySelectableSelected =
      selectableInvoiceIds.length > 0 &&
      selectableInvoiceIds.every(id => selectedInvoices.has(id));

    if (allCurrentlySelectableSelected) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(selectableInvoiceIds));
    }
  };

  const handleSelectAllDraftRCTIs = () => {
    // Filter RCTIs that can be selected: any RCTI not yet sent to Xero
    const selectableRCTIs = rctis.filter(
      rcti => rcti.xero_submission_status !== 'success'
    );
    const selectableRCTIIds = selectableRCTIs.map(rcti => rcti.id);

    const allCurrentlySelectableSelected =
      selectableRCTIIds.length > 0 &&
      selectableRCTIIds.every(id => selectedRCTIs.has(id));

    if (allCurrentlySelectableSelected) {
      setSelectedRCTIs(new Set());
    } else {
      setSelectedRCTIs(new Set(selectableRCTIIds));
    }
  };

  const handleSendInvoicesToXero = async () => {
    if (selectedInvoices.size === 0) return;

    setSendingInvoicesToXero(true);
    setInvoiceError(null);
    setInvoiceXeroBatchResponse(null);

    try {
      const invoiceIdsToSend = Array.from(selectedInvoices);
      const response = await sendMultipleDraftInvoicesToXero(invoiceIdsToSend);

      setInvoiceXeroBatchResponse(response);
      setInfoModalContent({
        title: response.success ? "Batch Send Invoices to Xero" : "Batch Send Invoices to Xero - Issues",
        message: response.message || "Invoices processed. Check details in the Xero dashboard.",
      });
      setIsInfoModalOpen(true);

      setSelectedInvoices(new Set());
      fetchInvoices();
    } catch (err) {
      console.error("Error sending batch invoices to Xero:", err);
      setInfoModalContent({
        title: "Error Sending Invoices to Xero",
        message: err.message || "An unexpected error occurred while sending invoices to Xero."
      });
      setIsInfoModalOpen(true);
    } finally {
      setSendingInvoicesToXero(false);
    }
  };

  const handleInvoicePageChange = async (newPage) => {
    // Don't allow navigation below page 1
    if (newPage < 1) {
      return;
    }
    // Don't allow going forward if there's no next page
    if (newPage > invoiceCurrentPage && !invoiceHasNextPage) {
      return;
    }
    
    setInvoicePageNavigating(true);
    const scrollPosition = window.scrollY;
    
    try {
      const startAfterDocId = invoicePageDocIds[newPage - 1];
      const params = {
        pageSize: invoicePageSize,
        orderBy: 'InvoiceDate',
        orderDirection: invoiceSortDirection,
        startAfterDocId,
      };
      if (selectedInvoiceStatus) {
        params.status = selectedInvoiceStatus;
      }
      if (invoiceFilterFseCode) {
        params.fseCode = invoiceFilterFseCode;
      }
      if (invoiceFilterHouseholdId) {
        params.householdId = invoiceFilterHouseholdId;
      }
      const data = await listInvoices(params);
      
      // Only update if we got data OR if going backward (data should exist)
      if (data.invoices.length > 0 || newPage < invoiceCurrentPage) {
        setInvoices(data.invoices);
        setInvoiceHasNextPage(!!data.nextPageDocId);
        setInvoiceCurrentPage(newPage);
        setSelectedInvoices(new Set());
        setInvoiceError(null); // Clear any previous errors on successful navigation
        
        // Update page doc IDs for next page navigation
        if (data.nextPageDocId && !invoicePageDocIds[newPage]) {
          setInvoicePageDocIds(prev => {
            const newDocIds = [...prev];
            newDocIds[newPage] = data.nextPageDocId;
            return newDocIds;
          });
        }
        setTimeout(() => window.scrollTo(0, scrollPosition), 0);
      } else {
        // No data - don't change anything, just show error
        setInvoiceError('No more invoices available.');
        setInvoiceHasNextPage(false);
      }
    } catch (err) {
      setInvoiceError(err.message || 'Failed to load invoices.');
    } finally {
      setInvoicePageNavigating(false);
    }
  };

  const handleInvoicePageSizeChange = (newPageSize) => {
    setInvoicePageSize(newPageSize);
    setInvoiceCurrentPage(1);
    setInvoicePageDocIds([null]);
    setInvoiceHasNextPage(false);
    setSelectedInvoices(new Set());
  };

  const handleRCTIPageChange = async (newPage) => {
    // Don't allow navigation below page 1
    if (newPage < 1) {
      return;
    }
    // Don't allow going forward if there's no next page
    if (newPage > rctiCurrentPage && !rctiHasNextPage) {
      return;
    }
    
    setRCTIPageNavigating(true);
    const scrollPosition = window.scrollY;
    
    try {
      const startAfterDocId = rctiPageDocIds[newPage - 1];
      const params = {
        pageSize: rctiPageSize,
        orderBy: 'RCTIDate',
        orderDirection: rctiSortDirection,
        startAfterDocId,
      };
      if (selectedRCTIStatus) {
        params.status = selectedRCTIStatus;
      }
      if (rctiFilterFseCode) {
        params.fseCode = rctiFilterFseCode;
      }
      if (rctiFilterHouseholdId) {
        params.householdId = rctiFilterHouseholdId;
      }
      if (rctiFilterXeroStatus) {
        params.xeroStatus = rctiFilterXeroStatus;
      }
      if (rctiFilterRctiNumber) {
        params.rctiNumber = rctiFilterRctiNumber;
      }
      const data = await listRCTIs(params);
      
      // Only update if we got data OR if going backward (data should exist)
      if (data.rctis.length > 0 || newPage < rctiCurrentPage) {
        setRCTIs(data.rctis);
        setRCTIHasNextPage(!!data.nextPageDocId);
        setRCTICurrentPage(newPage);
        setSelectedRCTIs(new Set());
        setRCTIError(null); // Clear any previous errors on successful navigation
        
        // Update page doc IDs for next page navigation
        if (data.nextPageDocId && !rctiPageDocIds[newPage]) {
          setRCTIPageDocIds(prev => {
            const newDocIds = [...prev];
            newDocIds[newPage] = data.nextPageDocId;
            return newDocIds;
          });
        }
        setTimeout(() => window.scrollTo(0, scrollPosition), 0);
      } else {
        // No data - don't change anything, just show error
        setRCTIError('No more RCTIs available.');
        setRCTIHasNextPage(false);
      }
    } catch (err) {
      setRCTIError(err.message || 'Failed to load RCTIs.');
    } finally {
      setRCTIPageNavigating(false);
    }
  };

  const handleRCTIPageSizeChange = (newPageSize) => {
    setRCTIPageSize(newPageSize);
    setRCTICurrentPage(1);
    setRCTIPageDocIds([null]);
    setRCTIHasNextPage(false);
    setSelectedRCTIs(new Set());
  };

  const handleSendRCTIsToXero = async () => {
    if (selectedRCTIs.size === 0) return;

    setSendingRCTIsToXero(true);
    setRCTIError(null);
    setRCTIXeroBatchResponse(null);

    try {
      const rctiIdsToSend = Array.from(selectedRCTIs);
      // Filter out already-sent items
      const validRctisToSend = rctis
        .filter(rcti => rctiIdsToSend.includes(rcti.id) && rcti.xero_submission_status !== 'success')
        .map(rcti => rcti.id);

      if (validRctisToSend.length === 0) {
        setInfoModalContent({
          title: "Send RCTIs to Xero - No Valid Items",
          message: "All selected RCTIs have already been sent to Xero successfully."
        });
        setIsInfoModalOpen(true);
        setSendingRCTIsToXero(false);
        return;
      }

      if (validRctisToSend.length < rctiIdsToSend.length) {
        const skippedCount = rctiIdsToSend.length - validRctisToSend.length;
        setInfoModalContent({
          title: "Send RCTIs to Xero - Some Items Skipped",
          message: `${skippedCount} selected RCTI(s) were skipped because they have already been sent to Xero successfully. Processing ${validRctisToSend.length} remaining RCTI(s).`
        });
        setIsInfoModalOpen(true);
      }

      const response = await sendMultipleDraftRCTIsToXero(validRctisToSend);

      setRCTIXeroBatchResponse(response);
      setInfoModalContent({
        title: response.success ? "Batch Send RCTIs to Xero" : "Batch Send RCTIs to Xero - Issues",
        message: response.message || "RCTIs processed. Check details in the Xero dashboard.",
      });
      setIsInfoModalOpen(true);

      setSelectedRCTIs(new Set());
      fetchRCTIs();
    } catch (err) {
      console.error("Error sending batch RCTIs to Xero:", err);
      setInfoModalContent({
        title: "Error Sending RCTIs to Xero",
        message: err.message || "An unexpected error occurred while sending RCTIs to Xero."
      });
      setIsInfoModalOpen(true);
    } finally {
      setSendingRCTIsToXero(false);
    }
  };

  const handleSyncInvoicesWithXero = async () => {
    const syncableInvoices = invoices
      .filter(inv => inv.xero_submission_status === 'success' && inv.xeroInvoiceId)
      .map(inv => inv.id);

    if (syncableInvoices.length === 0) {
      setInvoiceError("No invoices available for syncing with Xero.");
      return;
    }

    setInvoiceSyncing(true);
    setInvoiceError(null);
    setInvoiceSyncResults(null);

    try {
      const response = await syncMultipleInvoicesWithXero(syncableInvoices);
      
      if (response.responseStatus === 204 || response.status === 'success') {
        setInvoiceSyncResults(response.results);
        setIsSyncResultsModalOpen(true);
        await fetchInvoices();
      }
    } catch (err) {
      console.error('Error syncing invoices with Xero:', err);
      setInvoiceError(err.message || 'Failed to sync invoices with Xero');
    } finally {
      setInvoiceSyncing(false);
    }
  };

  // Opens the void confirmation modal
  const handleVoidInvoice = (invoiceId, invoiceNumber) => {
    setVoidConfirmModal({ isOpen: true, invoiceId, invoiceNumber });
  };

  // Confirms and executes the void action
  const confirmVoidInvoice = async () => {
    const { invoiceId } = voidConfirmModal;
    setVoidConfirmModal({ isOpen: false, invoiceId: null, invoiceNumber: null });
    setVoidingInvoiceId(invoiceId);
    setInvoiceError(null);

    try {
      await voidInvoice(invoiceId);
      setInfoModalContent({
        title: "Invoice Voided",
        message: "The invoice has been voided successfully."
      });
      setIsInfoModalOpen(true);
      // Refresh the invoice list
      await fetchInvoices(invoiceCurrentPage);
    } catch (err) {
      console.error('Error voiding invoice:', err);
      setInfoModalContent({
        title: "Error Voiding Invoice",
        message: err.message || 'Failed to void invoice'
      });
      setIsInfoModalOpen(true);
    } finally {
      setVoidingInvoiceId(null);
    }
  };

  // Opens the RCTI void confirmation modal
  const handleVoidRCTI = (rctiId, rctiNumber) => {
    setVoidRCTIConfirmModal({ isOpen: true, rctiId, rctiNumber });
  };

  // Confirms and executes the RCTI void action
  const confirmVoidRCTI = async () => {
    const { rctiId } = voidRCTIConfirmModal;
    setVoidRCTIConfirmModal({ isOpen: false, rctiId: null, rctiNumber: null });
    setVoidingRCTIId(rctiId);
    setRCTIError(null);

    try {
      await voidRCTI(rctiId);
      setInfoModalContent({
        title: "RCTI Voided",
        message: "The RCTI has been voided successfully."
      });
      setIsInfoModalOpen(true);
      // Refresh the RCTI list
      await fetchRCTIs(rctiCurrentPage);
    } catch (err) {
      console.error('Error voiding RCTI:', err);
      setInfoModalContent({
        title: "Error Voiding RCTI",
        message: err.message || 'Failed to void RCTI'
      });
      setIsInfoModalOpen(true);
    } finally {
      setVoidingRCTIId(null);
    }
  };

  const handleSyncRCTIsWithXero = async () => {
    const syncableRCTIs = rctis
      .filter(rcti => rcti.xero_submission_status === 'success' && rcti.xeroInvoiceId)
      .map(rcti => rcti.id);

    if (syncableRCTIs.length === 0) {
      setRCTIError("No RCTIs available for syncing with Xero.");
      return;
    }

    setRCTISyncing(true);
    setRCTIError(null);
    setRCTISyncResults(null);

    try {
      const response = await syncMultipleRCTIsWithXero(syncableRCTIs);
      
      if (response.responseStatus === 204 || response.status === 'success') {
        setRCTISyncResults(response.results);
        setIsSyncResultsModalOpen(true);
        await fetchRCTIs();
      }
    } catch (err) {
      console.error('Error syncing RCTIs with Xero:', err);
      setRCTIError(err.message || 'Failed to sync RCTIs with Xero');
    } finally {
      setRCTISyncing(false);
    }
  };

  const handleInvoiceRowClick = (invoiceId) => {
    navigate(`/invoices/${invoiceId}`, { state: { returnSearch: location.search } });
  };

  const handleRCTIRowClick = (rctiId) => {
    navigate(`/rctis/${rctiId}?returnTab=rctis`, { state: { returnSearch: location.search } });
  };

  const handleInvoiceXeroStatusClick = (invoice) => {
    if (invoice.xero_submission_status === 'failed' && invoice.xero_submission_failure_reason) {
      setInfoModalContent({
        title: "Invoice Xero Submission Failed",
        message: invoice.xero_submission_failure_reason
      });
      setIsInfoModalOpen(true);
    }
  };

  const handleCompletionDateClick = (jobId) => {
    if (jobId) {
      navigate(`/jobs-list/edit/${jobId}`, { state: { returnSearch: location.search } });
    }
  };

  const handleRCTIXeroStatusClick = (rcti) => {
    if (rcti.xero_submission_status === 'failed' && rcti.xero_submission_failure_reason) {
      setInfoModalContent({
        title: "RCTI Xero Submission Failed",
        message: rcti.xero_submission_failure_reason
      });
      setIsInfoModalOpen(true);
    }
  };

  // Invoice filter and sort handlers
  const handleInvoiceFilterChange = (filterType, value, options = {}) => {
    if (filterType === 'fseCode') {
      setInvoiceFilterFseCode(value);
    } else if (filterType === 'householdId') {
      setInvoiceFilterHouseholdId(value);
    } else if (filterType === 'xeroStatus') {
      setInvoiceFilterXeroStatus(value || '');
      applyInvoiceFilters({ xeroStatus: value || '' });
      return;
    } else if (filterType === 'invoiceNumber') {
      setInvoiceFilterInvoiceNumber(value || '');
    } else if (filterType === 'apply') {
      applyInvoiceFilters();
      return;
    }

    if (options.autoApply) {
      applyInvoiceFilters({ [filterType]: value });
    }
  };

  const handleInvoiceClearFilters = () => {
    setInvoiceFilterFseCode(null);
    setInvoiceFilterHouseholdId(null);
    setSelectedInvoiceStatus('');
    setInvoiceFilterXeroStatus('');
    setInvoiceFilterInvoiceNumber('');
    setInvoiceSortDirection('desc');
    applyInvoiceFilters({
      status: '',
      fseCode: null,
      householdId: null,
      sortDirection: 'desc',
      xeroStatus: '',
      invoiceNumber: '',
    });
  };

  const handleInvoiceSortDirectionChange = (direction) => {
    setInvoiceSortDirection(direction);
    applyInvoiceFilters({ sortDirection: direction });
  };

  // RCTI filter and sort handlers
  const handleRCTIFilterChange = (filterType, value, options = {}) => {
    if (filterType === 'fseCode') {
      setRCTIFilterFseCode(value);
    } else if (filterType === 'householdId') {
      setRCTIFilterHouseholdId(value);
    } else if (filterType === 'xeroStatus') {
      setRCTIFilterXeroStatus(value || '');
      applyRCTIFilters({ xeroStatus: value || '' });
      return;
    } else if (filterType === 'rctiNumber') {
      setRCTIFilterRctiNumber(value || '');
    } else if (filterType === 'apply') {
      applyRCTIFilters();
      return;
    }

    if (options.autoApply) {
      applyRCTIFilters({ [filterType]: value });
    }
  };

  const handleRCTIClearFilters = () => {
    setRCTIFilterFseCode(null);
    setRCTIFilterHouseholdId(null);
    setSelectedRCTIStatus('');
    setRCTISortDirection('desc');
    setRCTIFilterXeroStatus('');
    setRCTIFilterRctiNumber('');
    applyRCTIFilters({
      status: '',
      fseCode: null,
      householdId: null,
      sortDirection: 'desc',
      xeroStatus: '',
      rctiNumber: '',
    });
  };

  const handleRCTISortDirectionChange = (direction) => {
    setRCTISortDirection(direction);
    applyRCTIFilters({ sortDirection: direction });
  };

  // Franchisee Invoice handlers
  const fetchFranchiseeInvoices = useCallback(async (page = 1, filters = {}) => {
    setFranchiseeInvoiceLoading(true);
    setFranchiseeInvoiceError(null);

    const scrollPosition = window.scrollY;

    try {
      const startAfterDocId = franchiseeInvoicePageDocIds[page - 1];

      const effectiveFilters = {
        fseCode: filters.fseCode !== undefined ? filters.fseCode : franchiseeInvoiceFilterFseCode,
        status: filters.status !== undefined ? filters.status : franchiseeInvoiceFilterStatus,
        sortDirection: filters.sortDirection !== undefined ? filters.sortDirection : franchiseeInvoiceSortDirection,
      };

      const params = {
        pageSize: franchiseeInvoicePageSize,
        orderBy: 'Date',
        orderDirection: effectiveFilters.sortDirection,
        startAfterDocId,
      };

      if (effectiveFilters.fseCode) {
        params.fseCode = effectiveFilters.fseCode;
      }
      if (effectiveFilters.status) {
        params.status = effectiveFilters.status;
      }

      const data = await listFranchiseeInvoices(params);

      setFranchiseeInvoices(data.invoices);
      setFranchiseeInvoiceHasNextPage(!!data.nextPageDocId);

      if (data.nextPageDocId && !franchiseeInvoicePageDocIds[page]) {
        setFranchiseeInvoicePageDocIds(prev => {
          const newDocIds = [...prev];
          newDocIds[page] = data.nextPageDocId;
          return newDocIds;
        });
      }
    } catch (err) {
      setFranchiseeInvoiceError(err.message || 'Failed to load franchisee invoices.');
    } finally {
      setFranchiseeInvoiceLoading(false);
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    }
  }, [franchiseeInvoicePageSize, franchiseeInvoicePageDocIds, franchiseeInvoiceFilterFseCode, franchiseeInvoiceFilterStatus, franchiseeInvoiceSortDirection]);

  const applyFranchiseeInvoiceFilters = useCallback((filterOverrides = {}) => {
    resetFranchiseeInvoicePagination();
    persistFranchiseeInvoiceFiltersToSearchParams(filterOverrides);
    fetchFranchiseeInvoices(1, filterOverrides);
  }, [fetchFranchiseeInvoices, persistFranchiseeInvoiceFiltersToSearchParams, resetFranchiseeInvoicePagination]);

  const handleFranchiseeInvoicePageChange = async (newPage) => {
    if (newPage < 1) return;

    setFranchiseeInvoicePageNavigating(true);
    const scrollPosition = window.scrollY;

    try {
      const startAfterDocId = franchiseeInvoicePageDocIds[newPage - 1];

      const params = {
        pageSize: franchiseeInvoicePageSize,
        orderBy: 'Date',
        orderDirection: franchiseeInvoiceSortDirection,
        startAfterDocId,
      };

      if (franchiseeInvoiceFilterFseCode) {
        params.fseCode = franchiseeInvoiceFilterFseCode;
      }
      if (franchiseeInvoiceFilterStatus) {
        params.status = franchiseeInvoiceFilterStatus;
      }

      const data = await listFranchiseeInvoices(params);

      if (data.invoices.length > 0 || newPage < franchiseeInvoiceCurrentPage) {
        setFranchiseeInvoices(data.invoices);
        setFranchiseeInvoiceHasNextPage(!!data.nextPageDocId);
        setFranchiseeInvoiceCurrentPage(newPage);
        setFranchiseeInvoiceError(null);

        if (data.nextPageDocId && !franchiseeInvoicePageDocIds[newPage]) {
          setFranchiseeInvoicePageDocIds(prev => {
            const newDocIds = [...prev];
            newDocIds[newPage] = data.nextPageDocId;
            return newDocIds;
          });
        }
        setTimeout(() => window.scrollTo(0, scrollPosition), 0);
      } else {
        setFranchiseeInvoiceError('No more franchisee invoices available.');
        setFranchiseeInvoiceHasNextPage(false);
      }
    } catch (err) {
      setFranchiseeInvoiceError(err.message || 'Failed to load franchisee invoices.');
    } finally {
      setFranchiseeInvoicePageNavigating(false);
    }
  };

  const handleFranchiseeInvoicePageSizeChange = (newPageSize) => {
    setFranchiseeInvoicePageSize(newPageSize);
    setFranchiseeInvoiceCurrentPage(1);
    setFranchiseeInvoicePageDocIds([null]);
    setFranchiseeInvoiceHasNextPage(false);
  };

  const handleSyncFranchiseeInvoicesWithXero = async () => {
    setFranchiseeInvoiceSyncing(true);
    setFranchiseeInvoiceError(null);

    try {
      const response = await syncFranchiseeInvoicesWithXero();

      setInfoModalContent({
        title: "Sync Franchisee Invoices with Xero",
        message: `Successfully synced franchisee invoices.\n\nNew Invoices: ${response.newInvoices}\nUpdated Invoices: ${response.updatedInvoices}\nProcessed Franchisees: ${response.processedFranchisees}/${response.totalFranchisees}`
      });
      setIsInfoModalOpen(true);

      fetchFranchiseeInvoices();
    } catch (err) {
      console.error('Error syncing franchisee invoices with Xero:', err);
      setFranchiseeInvoiceError(err.message || 'Failed to sync franchisee invoices with Xero');
    } finally {
      setFranchiseeInvoiceSyncing(false);
    }
  };

  const handleOpenFranchiseeInvoiceSyncConfirm = () => {
    setIsFranchiseeInvoiceSyncConfirmOpen(true);
  };

  const handleConfirmFranchiseeInvoiceSync = async () => {
    setIsFranchiseeInvoiceSyncConfirmOpen(false);
    await handleSyncFranchiseeInvoicesWithXero();
  };

  const handleFranchiseeInvoiceRowClick = (invoiceId) => {
    navigate(`/invoices/${invoiceId}`, { state: { returnSearch: location.search } });
  };

  const handleFranchiseeInvoiceFilterChange = (filterType, value, options = {}) => {
    if (filterType === 'fseCode') {
      setFranchiseeInvoiceFilterFseCode(value);
    } else if (filterType === 'status') {
      setFranchiseeInvoiceFilterStatus(value);
    } else if (filterType === 'apply') {
      applyFranchiseeInvoiceFilters();
      return;
    }

    if (options.autoApply) {
      applyFranchiseeInvoiceFilters({ [filterType]: value });
    }
  };

  const handleFranchiseeInvoiceClearFilters = () => {
    setFranchiseeInvoiceFilterFseCode(null);
    setFranchiseeInvoiceFilterStatus('');
    setFranchiseeInvoiceSortDirection('desc');
    applyFranchiseeInvoiceFilters({
      fseCode: null,
      status: '',
      sortDirection: 'desc'
    });
  };

  const handleFranchiseeInvoiceSortDirectionChange = (direction) => {
    setFranchiseeInvoiceSortDirection(direction);
    applyFranchiseeInvoiceFilters({ sortDirection: direction });
  };

  // Calculate helper variables for invoices
  const isSendInvoicesToXeroDisabled = selectedInvoices.size === 0;
  const draftInvoicesCount = invoices.filter(inv => inv.status === 'draft').length;
  const selectableDraftInvoices = invoices.filter(
    inv => inv.status === 'draft' && inv.xero_submission_status !== 'success'
  );
  const selectableDraftInvoicesCount = selectableDraftInvoices.length;
  const allDraftInvoicesSelected =
    selectableDraftInvoicesCount > 0 &&
    selectableDraftInvoices.every(inv => selectedInvoices.has(inv.id));

  // Calculate helper variables for RCTIs
  const isSendRCTIsToXeroDisabled = selectedRCTIs.size === 0;
  const draftRCTIsCount = rctis.filter(rcti => rcti.status === 'draft').length;
  const selectableRCTIs = rctis.filter(
    rcti => rcti.xero_submission_status !== 'success'
  );
  const selectableDraftRCTIsCount = selectableRCTIs.length;
  const allDraftRCTIsSelected =
    selectableDraftRCTIsCount > 0 &&
    selectableRCTIs.every(rcti => selectedRCTIs.has(rcti.id));

  const currentError = activeTab === 'invoices' ? invoiceError : rctiError;
  const currentXeroBatchResponse = activeTab === 'invoices' ? invoiceXeroBatchResponse : rctiXeroBatchResponse;

  // RCTI Duplicate detection handlers
  const handleCheckRctiDuplicates = async () => {
    setCheckingRctiDuplicates(true);
    try {
      const result = await checkForDuplicates();
      if (result.status === 'success') {
        await fetchRctiDuplicatesList();
        if (result.duplicatesFound > 0) {
          // Directly open the duplicates modal instead of info modal
          setIsRctiDuplicatesModalOpen(true);
        } else {
          setInfoModalContent({
            title: 'No Duplicates Found',
            message: 'All RCTIs are unique. No duplicate payments detected.'
          });
          setIsInfoModalOpen(true);
        }
        // Refresh RCTIs to show updated duplicate flags
        fetchRCTIs(rctiCurrentPage);
      } else {
        setInfoModalContent({
          title: 'Error Checking for Duplicates',
          message: result.message || 'Failed to check for duplicates'
        });
        setIsInfoModalOpen(true);
      }
    } catch (error) {
      console.error('Error checking for RCTI duplicates:', error);
      setInfoModalContent({
        title: 'Error',
        message: error.message || 'An error occurred while checking for duplicates'
      });
      setIsInfoModalOpen(true);
    } finally {
      setCheckingRctiDuplicates(false);
    }
  };

  const fetchRctiDuplicatesList = async () => {
    try {
      const result = await listDuplicates('rcti');
      if (result.status === 'success') {
        setRctiDuplicates(result.duplicates || []);
        setRctiDuplicatesCount(result.count || 0);
      }
    } catch (error) {
      console.error('Error fetching RCTI duplicates list:', error);
    }
  };

  const handleOpenRctiDuplicatesModal = () => {
    setIsRctiDuplicatesModalOpen(true);
  };

  const handleCloseRctiDuplicatesModal = () => {
    setIsRctiDuplicatesModalOpen(false);
  };

  const handleRctiDuplicatesDismissed = async () => {
    // Refresh the duplicates list
    await fetchRctiDuplicatesList();
    // Refresh RCTIs to update the duplicate flags
    fetchRCTIs(rctiCurrentPage);
  };

  // Invoice Duplicate detection handlers
  const handleCheckInvoiceDuplicates = async () => {
    setCheckingInvoiceDuplicates(true);
    try {
      const result = await checkForInvoiceDuplicates();
      if (result.status === 'success') {
        await fetchInvoiceDuplicatesList();
        if (result.duplicatesFound > 0) {
          // Directly open the duplicates modal instead of info modal
          setIsInvoiceDuplicatesModalOpen(true);
        } else {
          setInfoModalContent({
            title: 'No Duplicates Found',
            message: 'All Invoices are unique. No duplicate billings detected.'
          });
          setIsInfoModalOpen(true);
        }
        // Refresh Invoices to show updated duplicate flags
        fetchInvoices(invoiceCurrentPage);
      } else {
        setInfoModalContent({
          title: 'Error Checking for Duplicates',
          message: result.message || 'Failed to check for duplicates'
        });
        setIsInfoModalOpen(true);
      }
    } catch (error) {
      console.error('Error checking for invoice duplicates:', error);
      setInfoModalContent({
        title: 'Error',
        message: error.message || 'An error occurred while checking for duplicates'
      });
      setIsInfoModalOpen(true);
    } finally {
      setCheckingInvoiceDuplicates(false);
    }
  };

  const fetchInvoiceDuplicatesList = async () => {
    try {
      const result = await listDuplicates('invoice');
      if (result.status === 'success') {
        setInvoiceDuplicates(result.duplicates || []);
        setInvoiceDuplicatesCount(result.count || 0);
      }
    } catch (error) {
      console.error('Error fetching invoice duplicates list:', error);
    }
  };

  const handleOpenInvoiceDuplicatesModal = () => {
    setIsInvoiceDuplicatesModalOpen(true);
  };

  const handleCloseInvoiceDuplicatesModal = () => {
    setIsInvoiceDuplicatesModalOpen(false);
  };

  const handleInvoiceDuplicatesDismissed = async () => {
    // Refresh the duplicates list
    await fetchInvoiceDuplicatesList();
    // Refresh Invoices to update the duplicate flags
    fetchInvoices(invoiceCurrentPage);
  };

  // Fetch duplicates list on mount based on active tab
  useEffect(() => {
    if (activeTab === 'rctis') {
      fetchRctiDuplicatesList();
    } else if (activeTab === 'invoices') {
      fetchInvoiceDuplicatesList();
    }
  }, [activeTab]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl flex items-center">
          <FaFileInvoiceDollar className="mr-3" /> Invoices & RCTIs
        </h1>
          <div className="flex items-center space-x-4">
          {autoSyncTab && (
            <div className="flex items-center text-jims-cyan-dark">
              <FaSync className="animate-spin mr-1" size="14" />
              <span className="text-sm font-medium">Syncing with Xero in progress....</span>
            </div>
          )}
          {/* Xero Status Indicator — hidden in demo mode */}
          {!isDemo && (
          <div className="flex items-center space-x-2">
            {xeroConnected === null ? (
              <div className="flex items-center text-gray-500">
                <FaSync className="animate-spin mr-1" size="14" />
                <span className="text-sm">Checking Xero...</span>
              </div>
            ) : xeroConnected ? (
              <div className="flex items-center text-green-600">
                <FaCheckCircle className="mr-1" size="14" />
                <span className="text-sm font-medium">Xero Connected</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <div className="flex items-center text-red-600">
                  <FaTimesCircle className="mr-1" size="14" />
                  <span className="text-sm font-medium">Xero Disconnected</span>
                </div>
                <button
                  onClick={handleConnectXero}
                  disabled={isConnectingToXero}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 shadow-sm transition duration-300"
                >
                  {isConnectingToXero ? (
                    <>
                      <FaSync className="animate-spin mr-1" size="12" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <FaLink className="mr-1" size="12" />
                      Connect
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          )}
          <Link to="/" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                clearInvoiceFilters({ updateSearchParams: false });
                setActiveTab('invoices');
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.delete('tab'); // Remove tab param to use default
                  // Clear all filter params on tab switch
                  ['invoiceStatus', 'invoiceFseCode', 'invoiceHouseholdId', 'invoiceClientId', 'invoiceSort',
                   'rctiStatus', 'rctiFseCode', 'rctiHouseholdId', 'rctiClientId', 'rctiSort',
                   'franchiseeInvoiceFseCode', 'franchiseeInvoiceStatus', 'franchiseeInvoiceSort'].forEach(key => newParams.delete(key));
                  return newParams;
                });
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-jims-cyan text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Supplier Invoices
            </button>
            <button
              onClick={() => {
                clearRCTIFilters({ updateSearchParams: false });
                setActiveTab('rctis');
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('tab', 'rctis');
                  // Clear all filter params on tab switch
                  ['invoiceStatus', 'invoiceFseCode', 'invoiceHouseholdId', 'invoiceClientId', 'invoiceSort',
                   'rctiStatus', 'rctiFseCode', 'rctiHouseholdId', 'rctiClientId', 'rctiSort',
                   'franchiseeInvoiceFseCode', 'franchiseeInvoiceStatus', 'franchiseeInvoiceSort'].forEach(key => newParams.delete(key));
                  return newParams;
                });
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rctis'
                  ? 'border-jims-cyan text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              RCTIs
            </button>
            <button
              onClick={() => {
                clearFranchiseeInvoiceFilters({ updateSearchParams: false });
                setActiveTab('franchisee-invoices');
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('tab', 'franchisee-invoices');
                  // Clear all filter params on tab switch
                  ['invoiceStatus', 'invoiceFseCode', 'invoiceHouseholdId', 'invoiceClientId', 'invoiceSort',
                   'rctiStatus', 'rctiFseCode', 'rctiHouseholdId', 'rctiClientId', 'rctiSort',
                   'franchiseeInvoiceFseCode', 'franchiseeInvoiceStatus', 'franchiseeInvoiceSort'].forEach(key => newParams.delete(key));
                  return newParams;
                });
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'franchisee-invoices'
                  ? 'border-jims-cyan text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Franchisee Invoices
            </button>
          </nav>
        </div>
      </div>

      {/* Xero Batch Response Display */}
      {currentXeroBatchResponse && !isInfoModalOpen && (
        <div className={`mb-4 p-3 rounded ${currentXeroBatchResponse.success ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-yellow-100 text-yellow-700 border border-yellow-300'}`}>
          {currentXeroBatchResponse.message}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'invoices' && (
        <InvoiceTabContent
          invoices={invoices}
          loading={invoiceLoading}
          error={invoiceError}
          selectedInvoices={selectedInvoices}
          selectedStatus={selectedInvoiceStatus}
          sendingToXero={sendingInvoicesToXero}
          isSyncing={invoiceSyncing}
          isSendToXeroDisabled={isSendInvoicesToXeroDisabled}
          draftCount={draftInvoicesCount}
          selectableDraftsCount={selectableDraftInvoicesCount}
          allDraftSelected={allDraftInvoicesSelected}
          onStatusChange={handleInvoiceStatusChange}
          onSelectInvoice={handleSelectInvoice}
          onSelectAllDraft={handleSelectAllDraftInvoices}
          onSendToXero={handleSendInvoicesToXero}
          onSync={handleSyncInvoicesWithXero}
          onRowClick={handleInvoiceRowClick}
          onXeroStatusClick={handleInvoiceXeroStatusClick}
          onCompletionDateClick={handleCompletionDateClick}
          onVoidInvoice={handleVoidInvoice}
          voidingInvoiceId={voidingInvoiceId}
          currentPage={invoiceCurrentPage}
          pageSize={invoicePageSize}
          hasNextPage={invoiceHasNextPage}
          onPageChange={handleInvoicePageChange}
          onPageSizeChange={handleInvoicePageSizeChange}
          isNavigating={invoicePageNavigating}
          filterFseId={invoiceFilterFseCode}
          filterHouseholdId={invoiceFilterHouseholdId}
          filterXeroStatus={invoiceFilterXeroStatus}
          filterInvoiceNumber={invoiceFilterInvoiceNumber}
          sortDirection={invoiceSortDirection}
          onFilterChange={handleInvoiceFilterChange}
          onClearFilters={handleInvoiceClearFilters}
          onSortDirectionChange={handleInvoiceSortDirectionChange}
          duplicatesCount={invoiceDuplicatesCount}
          onCheckDuplicates={handleCheckInvoiceDuplicates}
          onViewDuplicates={handleOpenInvoiceDuplicatesModal}
          checkingDuplicates={checkingInvoiceDuplicates}
        />
      )}

      {activeTab === 'rctis' && (
        <RCTITabContent
          rctis={rctis}
          loading={rctiLoading}
          error={rctiError}
          selectedRCTIs={selectedRCTIs}
          selectedStatus={selectedRCTIStatus}
          sendingToXero={sendingRCTIsToXero}
          isSyncing={rctiSyncing}
          isSendToXeroDisabled={isSendRCTIsToXeroDisabled}
          draftCount={draftRCTIsCount}
          selectableDraftsCount={selectableDraftRCTIsCount}
          allDraftSelected={allDraftRCTIsSelected}
          onStatusChange={handleRCTIStatusChange}
          onSelectRCTI={handleSelectRCTI}
          onSelectAllDraft={handleSelectAllDraftRCTIs}
          onSendToXero={handleSendRCTIsToXero}
          onSync={handleSyncRCTIsWithXero}
          onRowClick={handleRCTIRowClick}
          onXeroStatusClick={handleRCTIXeroStatusClick}
          onRefreshData={() => fetchRCTIs(rctiCurrentPage)}
          currentPage={rctiCurrentPage}
          pageSize={rctiPageSize}
          hasNextPage={rctiHasNextPage}
          onPageChange={handleRCTIPageChange}
          onPageSizeChange={handleRCTIPageSizeChange}
          isNavigating={rctiPageNavigating}
          filterFseCode={rctiFilterFseCode}
          filterHouseholdId={rctiFilterHouseholdId}
          sortDirection={rctiSortDirection}
          filterXeroStatus={rctiFilterXeroStatus}
          filterRctiNumber={rctiFilterRctiNumber}
          onFilterChange={handleRCTIFilterChange}
          onClearFilters={handleRCTIClearFilters}
          onSortDirectionChange={handleRCTISortDirectionChange}
          onCompletionDateClick={handleCompletionDateClick}
          onVoidRCTI={handleVoidRCTI}
          duplicatesCount={rctiDuplicatesCount}
          onCheckDuplicates={handleCheckRctiDuplicates}
          onViewDuplicates={handleOpenRctiDuplicatesModal}
          checkingDuplicates={checkingRctiDuplicates}
          voidingRCTIId={voidingRCTIId}
        />
      )}

      {activeTab === 'franchisee-invoices' && (
        <FranchiseeInvoiceTabContent
          invoices={franchiseeInvoices}
          loading={franchiseeInvoiceLoading}
          error={franchiseeInvoiceError}
          isSyncing={franchiseeInvoiceSyncing}
          onSync={handleOpenFranchiseeInvoiceSyncConfirm}
          onRowClick={handleFranchiseeInvoiceRowClick}
          currentPage={franchiseeInvoiceCurrentPage}
          pageSize={franchiseeInvoicePageSize}
          hasNextPage={franchiseeInvoiceHasNextPage}
          onPageChange={handleFranchiseeInvoicePageChange}
          onPageSizeChange={handleFranchiseeInvoicePageSizeChange}
          isNavigating={franchiseeInvoicePageNavigating}
          filterFseCode={franchiseeInvoiceFilterFseCode}
          filterStatus={franchiseeInvoiceFilterStatus}
          sortDirection={franchiseeInvoiceSortDirection}
          onFilterChange={handleFranchiseeInvoiceFilterChange}
          onClearFilters={handleFranchiseeInvoiceClearFilters}
          onSortDirectionChange={handleFranchiseeInvoiceSortDirectionChange}
        />
      )}

      {/* Modals */}
      <InfoModal 
        isOpen={isInfoModalOpen} 
        onClose={() => {
          setIsInfoModalOpen(false);
          setInvoiceXeroBatchResponse(null);
          setRCTIXeroBatchResponse(null);
        }}
        title={infoModalContent.title}
        message={infoModalContent.message}
      />

      <SyncResultsModal
        isOpen={isSyncResultsModalOpen}
        onClose={() => {
          setIsSyncResultsModalOpen(false);
          setInvoiceSyncResults(null);
          setRCTISyncResults(null);
        }}
        results={activeTab === 'invoices' ? invoiceSyncResults : rctiSyncResults}
      />

      <ConfirmationModal
        isOpen={isFranchiseeInvoiceSyncConfirmOpen}
        onClose={() => setIsFranchiseeInvoiceSyncConfirmOpen(false)}
        onConfirm={handleConfirmFranchiseeInvoiceSync}
        title="Sync Franchisee Invoices"
        message="This will sync franchisee invoices with Xero and update the franchisee invoice list. Continue?"
        isLoading={franchiseeInvoiceSyncing}
        loadingText="Syncing..."
      />

      <ConfirmationModal
        isOpen={voidConfirmModal.isOpen}
        onClose={() => setVoidConfirmModal({ isOpen: false, invoiceId: null, invoiceNumber: null })}
        onConfirm={confirmVoidInvoice}
        title="Void Invoice"
        message={`Are you sure you want to void invoice ${voidConfirmModal.invoiceNumber || ''}? This action cannot be undone. If the invoice has been sent to Xero, it will also be voided there.`}
        confirmText="Void Invoice"
        variant="danger"
        isLoading={voidingInvoiceId === voidConfirmModal.invoiceId}
        loadingText="Voiding..."
      />

      <ConfirmationModal
        isOpen={voidRCTIConfirmModal.isOpen}
        onClose={() => setVoidRCTIConfirmModal({ isOpen: false, rctiId: null, rctiNumber: null })}
        onConfirm={confirmVoidRCTI}
        title="Void RCTI"
        message={`Are you sure you want to void RCTI ${voidRCTIConfirmModal.rctiNumber || ''}? This action cannot be undone. If the RCTI has been sent to Xero, it will also be voided there.`}
        confirmText="Void RCTI"
        variant="danger"
        isLoading={voidingRCTIId === voidRCTIConfirmModal.rctiId}
        loadingText="Voiding..."
      />

      <DuplicatesModal
        isOpen={isRctiDuplicatesModalOpen}
        onClose={handleCloseRctiDuplicatesModal}
        duplicates={rctiDuplicates}
        onDuplicatesDismissed={handleRctiDuplicatesDismissed}
        type="rcti"
      />

      <DuplicatesModal
        isOpen={isInvoiceDuplicatesModalOpen}
        onClose={handleCloseInvoiceDuplicatesModal}
        duplicates={invoiceDuplicates}
        onDuplicatesDismissed={handleInvoiceDuplicatesDismissed}
        type="invoice"
      />
    </div>
  );
};

export default InvoicesPage;
